/**
 * 처방 요약(상단) — 환자 나이·체중·판정 시각·출처·호출 사용자·처방 경고(성분 중복). 성별은 표시하지 않는다.
 * @spec:2026-09-29-처방검토-판정기록-조회화면 (상세 화면 개편)
 */
import React from "react";
import Tag from "components/atoms/Tag";
import { sourceLabel, pharmacyDisplay, formatPatientAge, formatWeight } from "utils/doseReviewLogs";
import type { DoseReviewLogListItem } from "services/api/doseReviewLogs/types";
import type { PrescriptionFinding } from "api/doseReviewService";
import { SummaryBox, SummaryRow, SummaryItem, SummaryKey, WarningLine } from "./layoutStyle";

export interface PrescriptionSummaryProps {
  requestedAt: string | null;
  source: string;
  log: Pick<DoseReviewLogListItem, "pharmacyUserId" | "pharmacyUserName">;
  patient: { ageMonths: number | null; weightKg: number | null } | null;
  prescriptionFindings: PrescriptionFinding[];
}

const warningTypeLabel = (type: string): string =>
  type === "duplicate_ingredient" ? "성분 중복" : type;

const PrescriptionSummary: React.FC<PrescriptionSummaryProps> = ({
  requestedAt,
  source,
  log,
  patient,
  prescriptionFindings,
}) => (
  <SummaryBox data-testid="dose-detail-summary">
    <SummaryRow>
      <SummaryItem data-testid="dose-detail-time">
        <SummaryKey>판정</SummaryKey>
        {String(requestedAt ?? "").slice(0, 16).replace("T", " ") || "—"}
      </SummaryItem>
      <Tag size="Small" variant="Tinted" color="Gray">
        {sourceLabel(source)}
      </Tag>
      <SummaryItem title={log.pharmacyUserId ?? undefined} data-testid="dose-detail-pharmacy">
        <SummaryKey>호출 사용자</SummaryKey>{" "}
        {pharmacyDisplay(log)}
      </SummaryItem>
    </SummaryRow>
    <SummaryRow>
      <SummaryItem data-testid="dose-detail-age">
        <SummaryKey>나이</SummaryKey>{" "}
        {formatPatientAge(patient?.ageMonths ?? null)}
      </SummaryItem>
      <SummaryItem data-testid="dose-detail-weight">
        <SummaryKey>체중</SummaryKey>{" "}
        {formatWeight(patient?.weightKg ?? null)}
      </SummaryItem>
    </SummaryRow>
    {prescriptionFindings.length > 0 && (
      <div data-testid="dose-summary-warnings">
        {prescriptionFindings.map((pf, i) => (
          <WarningLine key={i} data-testid="dose-summary-warning">
            {warningTypeLabel(pf.type)} · {pf.ingredient} · 줄{" "}
            {pf.itemIndexes.map((idx) => idx + 1).join(", ")}
          </WarningLine>
        ))}
      </div>
    )}
  </SummaryBox>
);

export default PrescriptionSummary;
